<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { ApiRequestError, getAdminIntake, resetAdminIntake } from '@/api/client'
import { useToasts } from '@/lib/toasts'
import type { AdminIntakeDetail } from '@/api/schemas'
import { describeEvent, flattenAnswers, formatBytes, formatLocalTime } from './intakeHelpers'

const props = defineProps<{ id: number }>()

const detail = ref<AdminIntakeDetail | null>(null)
const error = ref('')
const loading = ref(true)
const toasts = useToasts()
const resetDialog = ref<HTMLDialogElement | null>(null)
const resetBusy = ref(false)

const stepTitles = computed(
  () => new Map((detail.value?.responses ?? []).map((response) => [response.step, response.title])),
)

async function load() {
  loading.value = true
  error.value = ''
  try {
    detail.value = await getAdminIntake(props.id)
  } catch (cause) {
    error.value = cause instanceof ApiRequestError ? cause.message : 'Unable to load this intake.'
  } finally {
    loading.value = false
  }
}
async function openReset() {
  await nextTick()
  resetDialog.value?.showModal()
}
async function reset() {
  resetBusy.value = true
  error.value = ''
  try {
    await resetAdminIntake(props.id)
    resetDialog.value?.close()
    toasts.success('Answers reset. The link is unchanged.')
    await load()
  } catch (cause) {
    resetDialog.value?.close()
    error.value = cause instanceof ApiRequestError ? cause.message : 'Unable to reset this intake.'
  } finally {
    resetBusy.value = false
  }
}
watch(() => props.id, load)
onMounted(load)
</script>

<template>
  <main class="admin-page">
    <header class="admin-header admin-header-row">
      <div>
        <p class="eyebrow">Intake</p>
        <h1 class="display">{{ detail?.intake.client_name ?? 'Intake' }}</h1>
        <p>Viewing this page does not count as the client opening their link.</p>
      </div>
      <div class="header-actions">
        <RouterLink class="btn btn-secondary" to="/admin/intakes">All intake links</RouterLink
        ><button
          v-if="detail && detail.intake.status !== 'revoked'"
          class="btn btn-danger"
          type="button"
          @click="openReset"
        >
          Reset answers
        </button>
      </div>
    </header>
    <p v-if="error" class="form-error" role="alert">{{ error }}</p>
    <section v-if="loading" class="admin-card" aria-busy="true">Loading intake…</section>
    <template v-else-if="detail">
      <section class="admin-card detail-section">
        <h2>Status</h2>
        <dl class="detail-facts">
          <dt>Status</dt>
          <dd>
            <span class="status" :class="`status-${detail.intake.status}`">{{
              detail.intake.status
            }}</span>
          </dd>
          <dt>Template</dt>
          <dd>{{ detail.intake.form_type }}</dd>
          <dt>First opened</dt>
          <dd>{{ detail.first_opened_at ? formatLocalTime(detail.first_opened_at) : 'Never' }}</dd>
          <dt>Last activity</dt>
          <dd>{{ detail.last_activity_at ? formatLocalTime(detail.last_activity_at) : 'None' }}</dd>
          <dt>Submitted</dt>
          <dd>{{ detail.submitted_at ? formatLocalTime(detail.submitted_at) : 'Not yet' }}</dd>
          <dt>Created</dt>
          <dd>{{ formatLocalTime(detail.intake.created_at) }}</dd>
          <dt>Expires</dt>
          <dd>{{ formatLocalTime(detail.intake.expires_at) }}</dd>
        </dl>
      </section>

      <section class="admin-card detail-section">
        <h2>Answers</h2>
        <p v-if="!detail.responses.length" class="detail-empty">No steps saved yet.</p>
        <div v-for="response in detail.responses" :key="response.step" class="detail-step">
          <h3>Step {{ response.step + 1 }} — {{ response.title }}</h3>
          <p class="detail-meta">Saved {{ formatLocalTime(response.updated_at) }}</p>
          <dl class="detail-facts">
            <template
              v-for="(row, index) in flattenAnswers(response.data)"
              :key="`${response.step}-${index}`"
            >
              <dt>{{ row.label }}</dt>
              <dd>{{ row.value }}</dd>
            </template>
          </dl>
        </div>
      </section>

      <section class="admin-card detail-section">
        <h2>Documents</h2>
        <p v-if="!detail.files.length" class="detail-empty">No documents uploaded.</p>
        <p v-if="detail.intake.documents.missing_required.length" class="detail-meta">
          Missing required: {{ detail.intake.documents.missing_required.join(', ') }}
        </p>
        <ul v-if="detail.files.length" class="detail-list">
          <li v-for="(file, index) in detail.files" :key="index">
            <strong>{{ file.filename }}</strong> · {{ file.kind }} · {{ formatBytes(file.size) }} ·
            {{ formatLocalTime(file.uploaded_at) }}
          </li>
        </ul>
      </section>

      <section class="admin-card detail-section">
        <h2>Activity</h2>
        <p v-if="!detail.events.length" class="detail-empty">No activity yet.</p>
        <ol v-else class="detail-list">
          <li v-for="(event, index) in detail.events" :key="index">
            <span class="detail-meta">{{ formatLocalTime(event.created_at) }}</span>
            {{ describeEvent(event.event, event.detail, stepTitles) }}
          </li>
        </ol>
      </section>
    </template>
    <dialog ref="resetDialog" class="confirm-dialog" aria-labelledby="reset-title">
      <h2 id="reset-title">Reset {{ detail?.intake.client_name }}’s answers?</h2>
      <p>
        Saved answers are deleted and uploaded documents are removed from the form (their Drive
        files stay). A submitted intake reopens. The link stays the same, so the client can use the
        one you already sent.
      </p>
      <div class="dialog-actions">
        <button class="btn btn-secondary" :disabled="resetBusy" @click="resetDialog?.close()">
          Cancel</button
        ><button class="btn btn-danger" :disabled="resetBusy" @click="reset">
          {{ resetBusy ? 'Resetting…' : 'Reset answers' }}
        </button>
      </div>
    </dialog>
  </main>
</template>
